import {
    BehaviorSubject, merge, Observable, throwError, isObservable, of
} from "rxjs";
import {
    catchError, map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";

import {
    FileSaverOptions, saveAs
} from "file-saver";

import {
    IValidatedModel, IValidatedModelStatic, ModelUtil, SortUtil
} from "@shopliftr/common-js/data";

import { ICsvOptions } from "./interface/csv-options";
import { IField } from "./interface/field";
import { IImageSize } from "./interface/image-size";

import { ServiceException } from "../../exceptions/service-exception";

import { NotificationService } from "../../notification/services/notification.service";

/**
 * Used to generate files for download
 */
@Injectable({
    providedIn: "root"
})
export class FileService {
    private readonly _notificationService = inject(NotificationService);

    /**
     * The current state of the service
     */
    private readonly _downloadState$ = new BehaviorSubject<boolean>(false);

    /**
     * Determines if a file is currently being downloaded
     */
    get isDownloading(): boolean {

        return this._downloadState$.getValue();
    }

    /**
     * Public-facing state subscriber
     */
    get downloadState$(): Observable<boolean> {

        return this._downloadState$.asObservable();
    }

    /**
     * Saves the given blob as a file with the given name using the browser's native file-saver
     *
     * @param blob - The file content to save
     * @param fileName - The name to give the downloaded file
     * @param option - Optional file-saver configuration options
     */
    saveFile(blob: Blob, fileName: string, option?: FileSaverOptions): void {

        saveAs(blob, fileName, option);
    }

    /**
     * Generates a tab-delimited file from the given data. If the options do not included a set of desired fields,
     * the function will intelligently build one by reading through the input data.
     *
     * The delimiter and line delimiter can be configured using the options, but these options should be avoided
     * unless the integrity of the data can be verified.
     */
    generateTabDelimitedFile$(
        data$: Observable<Array<IValidatedModel<any>>> | Array<IValidatedModel<any>>,
        serializer: IValidatedModelStatic<any>,
        fileName: string,
        options?: ICsvOptions
    ): Observable<boolean> {

        if (this._downloadState$.getValue()) {

            return throwError(() => new ServiceException("Only one file can be downloaded at once."));
        }
        else {
            this._downloadState$.next(true);

            if (!isObservable(data$)) {
                data$ = of(data$);
            }

            return merge(data$, this._notificationService.displayPersistentMessage$("Preparing your file", "Cancel Download")).pipe(
                map((streamValue: Array<IValidatedModel<any>> | void): boolean => {

                    // Data stream completed
                    if (Array.isArray(streamValue)) {
                        const serializedData = ModelUtil.serializeArray(streamValue, serializer);

                        let delimiter = "\t";
                        let lineDelimiter = "\n";

                        let showHeaderRow = true;

                        let fields: Array<IField>;

                        if (options) {
                            delimiter = options.delimiter || delimiter;
                            lineDelimiter = options.lineDelimiter || lineDelimiter;

                            if (options.showHeaderRow !== undefined) {
                                showHeaderRow = options.showHeaderRow;
                            }

                            if (options.fields) {
                                fields = options.fields;
                            }

                            if (options.lineTransformer) {
                                serializedData.forEach((item: any) => {

                                    options.lineTransformer(item);
                                });
                            }
                        }

                        // If the desired fields are not explicitly provided, We need to loop through all of the items
                        // to ensure that we don't miss any optional fields in later items. Note that this forces alphabetical
                        // ordering
                        if (!fields?.length) {
                            fields = this._generateFieldSet(serializedData);
                        }

                        let output = "";

                        // Header
                        if (showHeaderRow) {
                            fields.forEach((field: IField, index: number) => {

                                // Default to the field name if no label is provided
                                output += field.label || field.fieldName;
                                output += (index < (fields.length - 1)) ? delimiter : lineDelimiter;
                            });
                        }

                        // Body
                        serializedData.forEach((item: IValidatedModel<any>) => {

                            fields.forEach((field: IField, index: number) => {

                                try {
                                    // isIterator handles the case of A: Array<B: { C: any }> -> A.B.C[]
                                    output += this._resolveNode(field.fieldName, item, field.isIterator);
                                }
                                catch (error) {
                                    // This type of error can only occur if the user has specifically provided the
                                    // target field, so the messaging is targeted at the developer
                                    throw new ServiceException(
                                        `${field.fieldName}: this field can't be displayed contains an object. You should`
                                        + `instead target the specific field you want to display. See examples for more details`
                                    );
                                }

                                output += (index < (fields.length - 1)) ? delimiter : lineDelimiter;
                            });
                        });

                        this._notificationService.dismissPersistent();

                        // Download file
                        const blob: Blob = new Blob([output], { type: "text/csv;charset=utf-8;" });

                        this._downloadState$.next(false);

                        // Hardcoding extension to .txt so that the tab delimiter can be correctly consumed
                        return this._downloadFile(blob, fileName);
                    }
                    // Download action explicitly cancelled
                    else {
                        this._notificationService.dismissPersistent();

                        this._downloadState$.next(false);

                        return false;
                    }
                }),
                catchError((error: unknown) => {

                    this._notificationService.dismissPersistent();

                    this._downloadState$.next(false);

                    return throwError(() => error);
                })
            );
        }
    }

    /**
     * Resolves the pixel dimensions of an image from either a URL or a Blob.
     *
     * Loads the image off-screen and reads its natural size. A Blob source gets a
     * temporary object URL, revoked once the image has loaded or failed.
     *
     * @param source - An image URL, or a Blob containing image data
     * @returns The image's pixel width and height
     */
    getImageSize$(source: string | Blob): Observable<IImageSize> {

        return new Observable<IImageSize>((subscriber) => {

            const objectUrl = typeof source === "string" ? undefined : URL.createObjectURL(source);
            const url = objectUrl ?? source as string;
            const image = new Image();

            const cleanUp = (): void => {

                image.onload = null;
                image.onerror = null;
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };

            image.onload = (): void => {

                const size: IImageSize = {
                    width: image.naturalWidth,
                    height: image.naturalHeight
                };
                cleanUp();
                subscriber.next(size);
                subscriber.complete();
            };

            image.onerror = (): void => {

                cleanUp();
                subscriber.error(new ServiceException(`Could not read image dimensions from the given ${objectUrl ? "blob" : "url"}`));
            };

            image.src = url;

            return cleanUp;
        });
    }

    /**
     * Generates a file and forces the browser to attempt to download it so the user can save it locally
     */
    private _downloadFile(blob: Blob, fileNameWithExtension: string): boolean {

        const link = document.createElement("a");

        link.href = URL.createObjectURL(blob);
        link.download = fileNameWithExtension;
        link.style.visibility = "hidden";

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        return true;
    }

    /**
     * Determines if the given field already exists in the field set
     */
    private _fieldAlreadyAdded(set: Array<IField>, newField: IField): boolean {

        const existingIndex = set.findIndex((field: IField) => {

            return field.fieldName === newField.fieldName;
        });

        return (existingIndex !== -1);
    }

    /**
     * Cycles through the given data to create a sorted set of fields which represent it in the output file
     */
    private _generateFieldSet(data: Record<string, any> | Array<Record<string, any>>, prefix?: string): Array<IField> {

        const output = new Array<IField>();

        let results: Array<IField>;

        if (Array.isArray(data)) {
            data.forEach((item: Record<string, any>) => {

                results = this._resolveFieldSet(item, prefix);

                results.forEach((result: IField) => {

                    if (!this._fieldAlreadyAdded(output, result)) {
                        output.push(result);
                    }
                });
            });
        }
        else {
            results = this._resolveFieldSet(data, prefix);

            results.forEach((result: IField) => {

                if (!this._fieldAlreadyAdded(output, result)) {
                    output.push(result);
                }
            });
        }

        return output.sort(SortUtil.compareFn("fieldName"));
    }

    /**
     * Processes the given data item (which is guaranteed to not be an array) and creates the actual field objects
     */
    private _resolveFieldSet(data: Record<string, any>, prefix?: string): Array<IField> {

        const output = new Array<IField>();

        let fieldName: string;

        Object.keys(data).forEach((key: string) => {

            fieldName = prefix ? `${prefix}.${key}` : key;

            if (typeof data[key] === "object" && !Array.isArray(data[key]) && data[key] !== null) {
                const results = this._generateFieldSet(data[key] as Record<string, any> | Array<Record<string, any>>, fieldName);

                results.forEach((result: IField) => {

                    if (!this._fieldAlreadyAdded(output, result)) {
                        output.push(result);
                    }
                });
            }
            else if (Array.isArray(data[key]) && data[key].length) {
                // Structure: A: [{ B, C }] -> A.B[], A.C[]
                if (typeof data[key][0] === "object") {
                    const subFields = new Set<string>();

                    (data[key] as Array<object>).forEach((subElement: object) => {

                        Object.keys(subElement).forEach((subKey: string) => {

                            subFields.add(subKey);
                        });
                    });

                    let resolvedFieldName: string;

                    subFields.forEach((subKey: string) => {

                        resolvedFieldName = `${fieldName}.${subKey}`;

                        const field = {
                            fieldName: resolvedFieldName,
                            isIterator: true
                        };

                        if (!this._fieldAlreadyAdded(output, field)) {
                            output.push(field);
                        }
                    });
                }
                else {
                    const field = { fieldName: fieldName };

                    if (!this._fieldAlreadyAdded(output, field)) {
                        output.push(field);
                    }
                }
            }
            else {
                const field = { fieldName: fieldName };

                if (!this._fieldAlreadyAdded(output, field)) {
                    output.push(field);
                }
            }
        });

        return output;
    }

    /**
     * Retrieves the value at the end of the given path
     */
    private _resolveNode(path: string, item: any, isIterator?: boolean): string {

        const fieldSet = path.split(".");

        let value: any = item;

        if (fieldSet.length === 1) {
            return this._resolveValue(value[fieldSet[0]]);
        }
        else {
            let iteratedField: string;

            if (isIterator) {
                iteratedField = fieldSet.pop();
            }

            // Iterated along the path to get to the final value
            fieldSet.forEach((key: string) => {

                // At every stage, we need to make sure that we're delving into an object to prevent an exception
                if (typeof value === "object" && value !== null) {
                    value = value[key];
                }
                else {
                    value = undefined;
                }
            });

            if (iteratedField) {
                if (value && Array.isArray(value)) {
                    const iteratedValues = new Array<any>();

                    value.forEach((targetObject: any) => {

                        if (targetObject[iteratedField] !== undefined && targetObject[iteratedField] !== null) {
                            iteratedValues.push(this._resolveValue(targetObject[iteratedField]));
                        }
                    });

                    return iteratedValues.join(",");
                }
                else {
                    return this._resolveValue(undefined);
                }
            }
            else {
                return this._resolveValue(value);
            }
        }
    }

    /**
     * Transforms the value into a string that will look valid in the output
     */
    private _resolveValue(value: any): string {

        if (typeof value === "object" && (!Array.isArray(value) || typeof value[0] === "object") && value !== null) {
            throw new ServiceException("Object encountered");
        }
        else {
            if (typeof value === "boolean") {
                return (value ? "Yes" : "No");
            }
            else if (typeof value === "number") {
                return value.toString();
            }
            else {
                // Prevents "undefined" from getting added to the output
                return (value || "");
            }
        }
    }
}
